if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
    }

    get selectedOptionValues() {
      const options = this.querySelectorAll('.m-product-option select, .m-product-option input');
      const selectedValues = new Map();

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (field.tagName.toLowerCase() === "select") {
          const selectedOption = Array.from(field.options).find(
            (opt) => opt.selected && !opt.disabled
          );
          if (selectedOption) {
            selectedValues.set(fieldName, selectedOption.dataset.optionValueId);
          }
        } else if (
          field.tagName.toLowerCase() === "input" &&
          field.type === "radio"
        ) {
          const checkedInput = field.checked ? field : null;
          if (checkedInput) {
            selectedValues.set(fieldName, checkedInput.dataset.optionValueId);
          }
        }
      });

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (!selectedValues.has(fieldName)) {
          if (field.tagName.toLowerCase() === "select") {
            const firstValidOption = Array.from(field.options).find(
              (opt) => !opt.disabled
            );
            if (firstValidOption) {
              selectedValues.set(
                fieldName,
                firstValidOption.dataset.optionValueId
              );
            }
          } else if (
            field.tagName.toLowerCase() === "input" &&
            field.type === "radio"
          ) {
            const firstInput = field;
            if (firstInput) {
              selectedValues.set(fieldName, firstInput.dataset.optionValueId);
            }
          }
        }
      });

      const result = [];
      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;
        if (selectedValues.has(fieldName)) {
          const optionValueId = selectedValues.get(fieldName);
          if (optionValueId && !result.includes(optionValueId)) {
            result.push(optionValueId);
          }
        }
      });
      return result;
    }

    connectedCallback() {
      this.addEventListener('change', (event) => {
        const target = this.getInputForEventTarget(event.target);
        MinimogEvents.emit(MinimogTheme.pubSubEvents.optionValueSelectionChange, { data: { selectedOptionValues: this.selectedOptionValues, event, target } });
        requestAnimationFrame(() => {
          this.updateSelectedFrameLabel();
          this.updateSelectedItalianOptionLabels();
          this.updateFrameColorSelector();
          if (this.ledColorSelector) {
            const currentLed = this.getSelectedLedValue();
            if (currentLed) this.selectLedColor(currentLed, false);
          }
        });
      });

      this.initFrameColorSelector();
      this.initLedColorSelector();
      this.updateSelectedItalianOptionLabels();
    }

    initLedColorSelector() {
      this.ledColorSelector = this.querySelector('[data-led-color-selector]');
      if (!this.ledColorSelector) return;

      this.ledColorProperty = this.querySelector('[data-led-color-property]');
      this.ledColorSelectedLabel = this.querySelector('[data-led-color-selected]');

      window.__grLedColorSelections = window.__grLedColorSelections || {};
      const productKey = this.dataset.productId || 'default';
      const currentValue = this.getSelectedLedValue();

      if (currentValue) {
        window.__grLedColorSelections[productKey] = currentValue;
      } else if (!window.__grLedColorSelections[productKey]) {
        window.__grLedColorSelections[productKey] = 'Bardhë';
      }

      this.ledColorSelection = window.__grLedColorSelections[productKey] || 'Bardhë';

      this.querySelectorAll('[data-led-color]').forEach((button) => {
        button.addEventListener('click', () => {
          this.selectLedColor(button.dataset.ledColor, true);
        });
      });

      this.selectLedColor(this.ledColorSelection, false);
    }

    getSelectedLedValue() {
      const ledOption = this.querySelector('[data-led-option="true"]');
      if (!ledOption) return '';

      const checked = ledOption.querySelector('input[type="radio"]:checked');
      if (checked) return this.normalizeLedColorValue((checked.value || '').trim());

      const select = ledOption.querySelector('select');
      return select ? this.normalizeLedColorValue((select.value || '').trim()) : '';
    }

    normalizeLedColorValue(value) {
      const canonical = { 'Weiß': 'Bardhë', 'Gelb': 'Verdhë', 'Rot': 'Kuqe', 'Blau': 'Blu', 'Grün': 'Jeshile', 'Rosa': 'Rozë' };
      return canonical[value] || value;
    }

    formatLedColorLabel(value) {
      const lang = (document.documentElement.lang || '').toLowerCase();
      if (lang.startsWith('it')) {
        const labels = {
        'Bardhë': 'Bianco',
        'Verdhë': 'Giallo',
        'Kuqe': 'Rosso',
        'Blu': 'Blu',
        'Jeshile': 'Verde',
        'Rozë': 'Rosa'
        };
        return labels[value] || value;
      }
      if (lang.startsWith('de')) {
        const labels = { 'Bardhë': 'Weiß', 'Verdhë': 'Gelb', 'Kuqe': 'Rot', 'Blu': 'Blau', 'Jeshile': 'Grün', 'Rozë': 'Rosa' };
        return labels[this.normalizeLedColorValue(value)] || value;
      }
      if (!lang.startsWith('en')) return value;
      const labels = {
        'Bardhë': 'White',
        'Verdhë': 'Yellow',
        'Kuqe': 'Red',
        'Blu': 'Blue',
        'Jeshile': 'Green',
        'Rozë': 'Pink'
      };
      return labels[value] || value;
    }

    selectLedColor(value, syncVariant) {
      if (!this.ledColorSelector || !value) return;

      const productKey = this.dataset.productId || 'default';
      window.__grLedColorSelections = window.__grLedColorSelections || {};
      window.__grLedColorSelections[productKey] = value;
      this.ledColorSelection = value;

      if (this.ledColorProperty) {
        this.ledColorProperty.value = value;
      }

      if (this.ledColorSelectedLabel) {
        this.ledColorSelectedLabel.textContent = this.formatLedColorLabel(value);
      }

      this.querySelectorAll('[data-led-color]').forEach((button) => {
        const selected = button.dataset.ledColor === value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      if (!syncVariant) return;

      const ledOption = this.querySelector('[data-led-option="true"]');
      if (!ledOption) return;

      const radio = Array.from(ledOption.querySelectorAll('input[type="radio"]')).find((input) => this.normalizeLedColorValue(input.value) === value);
      if (radio) {
        if (!radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      const select = ledOption.querySelector('select');
      if (select && select.value !== value) {
        const matchingOption = Array.from(select.options).find((option) => this.normalizeLedColorValue(option.value) === value);
        if (matchingOption) {
          select.value = matchingOption.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    initFrameColorSelector() {
      this.frameColorSelector = this.querySelector('[data-frame-color-selector]');
      if (!this.frameColorSelector) return;

      window.__grFrameColorSelections = window.__grFrameColorSelections || {};
      const productKey = this.dataset.productId || 'default';

      if (!window.__grFrameColorSelections[productKey]) {
        window.__grFrameColorSelections[productKey] = {
          standarte: 'E zezë',
          gold: 'Classic Gold'
        };
      }

      this.frameColorSelections = window.__grFrameColorSelections[productKey];
      this.frameColorProperty = this.querySelector('[data-frame-color-property]');
      this.frameColorSelectedLabel = this.querySelector('[data-frame-color-selected]');

      this.querySelectorAll('[data-frame-color]').forEach((button) => {
        button.addEventListener('click', () => {
          const group = button.dataset.frameGroup;
          const value = button.dataset.frameColor;
          this.selectFrameColor(group, value);
        });
      });

      this.updateFrameColorSelector();
    }

    updateSelectedFrameLabel() {
      const frameOption = this.querySelector('[data-frame-option="true"]');
      if (!frameOption) return;
      const label = frameOption.querySelector('.option-label--selected');
      const selected = this.getSelectedFrameValue();
      if (!label || !selected) return;
      const key = selected.toLowerCase().trim();
      const names = {
        'telajo': ['Canvas', 'Tela'],
        'canvas': ['Canvas', 'Tela'],
        'unframed canvas': ['Canvas', 'Tela'],
        'standarte': ['Floating Frame', 'Cornice flottante'],
        'standard': ['Floating Frame', 'Cornice flottante'],
        'floating frame': ['Floating Frame', 'Cornice flottante'],
        'gold klasike': ['Classic Gold Frame', 'Cornice classica oro'],
        'classic gold': ['Classic Gold Frame', 'Cornice classica oro'],
        'classic gold frame': ['Classic Gold Frame', 'Cornice classica oro']
      };
      const lang = (document.documentElement.lang || '').toLowerCase();
      const pair = names[key];
      const germanFrames = { 'telajo': 'Leinwand', 'canvas': 'Leinwand', 'unframed canvas': 'Leinwand', 'standarte': 'Schattenfugenrahmen', 'standard': 'Schattenfugenrahmen', 'floating frame': 'Schattenfugenrahmen', 'gold klasike': 'Klassischer Goldrahmen', 'classic gold': 'Klassischer Goldrahmen', 'classic gold frame': 'Klassischer Goldrahmen' };
      if (lang.startsWith('de')) label.textContent = germanFrames[key] || selected;
      else if (pair) label.textContent = lang.startsWith('it') ? pair[1] : lang.startsWith('en') ? pair[0] : selected;
    }

    formatItalianOptionValue(value) {
      const raw = String(value || '').trim();
      const translation = {
        'xham': 'Vetro',
        'brown': 'Marrone',
        'green': 'Verde',
        'orange': 'Arancione',
        'black': 'Nero',
        'white': 'Bianco',
        'yellow': 'Giallo',
        'red': 'Rosso'
      };
      return translation[raw.toLowerCase()] || raw.replace(/cm\s+secila\b/gi, ' cm ciascuno');
    }

    formatGermanOptionValue(value) {
      const raw = String(value || '').trim();
      const translation = { 'xham': 'Glas', 'brown': 'Braun', 'green': 'Grün', 'orange': 'Orange', 'black': 'Schwarz', 'white': 'Weiß', 'yellow': 'Gelb', 'red': 'Rot' };
      return translation[raw.toLowerCase()] || raw.replace(/cm\s+secila\b/gi, ' cm je Bild');
    }

    updateSelectedItalianOptionLabels() {
      const lang = (document.documentElement.lang || '').toLowerCase();
      if (!lang.startsWith('it') && !lang.startsWith('de')) return;

      this.querySelectorAll('.m-product-option').forEach((option) => {
        const name = option.querySelector('[data-option-name]')?.dataset.optionName;
        if (name !== 'Material' && name !== 'Ngjyra' && name !== '1. Zgjidh Përmasat' && name !== 'Farbe' && name !== '1. Größe wählen') return;
        const checked = option.querySelector('input[type="radio"]:checked');
        const dropdown = option.querySelector('select');
        const value = checked?.value || dropdown?.value || '';
        const label = option.querySelector('.option-label--selected');
        if (value && label) label.textContent = lang.startsWith('de') ? this.formatGermanOptionValue(value) : this.formatItalianOptionValue(value);
      });
    }

    getSelectedFrameValue() {
      const frameOption = this.querySelector('[data-frame-option="true"]');
      if (!frameOption) return '';

      const checked = frameOption.querySelector('input[type="radio"]:checked');
      if (checked) return (checked.value || '').trim();

      const select = frameOption.querySelector('select');
      return select ? (select.value || '').trim() : '';
    }

    formatFrameColorLabel(value) {
      const lang = (document.documentElement.lang || '').toLowerCase();
      if (lang.startsWith('it')) {
        const labels = {
        'E zezë': 'Nero',
        'E bardhë': 'Bianco',
        'Dru i errët': 'Legno scuro',
        'Dru i çelët': 'Legno chiaro',
        'Gold (e artë)': 'Oro',
        'Gri': 'Grigio',
        'Pana': 'Crema',
        'Classic Gold': 'Oro classico',
        'Gold Blue': 'Oro / Blu',
        'Gold Green': 'Oro / Verde',
        'Gold Silver': 'Oro / Argento'
        };
        return labels[value] || value;
      }
      if (lang.startsWith('de')) {
        const labels = { 'E zezë': 'Schwarz', 'E bardhë': 'Weiß', 'Dru i errët': 'Dunkles Holz', 'Dru i çelët': 'Helles Holz', 'Gold (e artë)': 'Gold', 'Gri': 'Grau', 'Pana': 'Creme', 'Classic Gold': 'Klassisches Gold', 'Gold Blue': 'Gold / Blau', 'Gold Green': 'Gold / Grün', 'Gold Silver': 'Gold / Silber' };
        return labels[value] || value;
      }
      if (!lang.startsWith('en')) return value;
      const labels = {
        'E zezë': 'Black',
        'E bardhë': 'White',
        'Dru i errët': 'Dark Wood',
        'Dru i çelët': 'Light Wood',
        'Gold (e artë)': 'Gold',
        'Gri': 'Gray',
        'Pana': 'Cream',
        'Classic Gold': 'Classic Gold',
        'Gold Blue': 'Gold Blue',
        'Gold Green': 'Gold Green',
        'Gold Silver': 'Gold Silver'
      };
      return labels[value] || value;
    }

    selectFrameColor(group, value) {
      if (!this.frameColorSelector || !group || !value) return;

      this.frameColorSelections[group] = value;

      if (this.frameColorProperty) {
        this.frameColorProperty.disabled = false;
        this.frameColorProperty.value = value;
      }

      if (this.frameColorSelectedLabel) {
        this.frameColorSelectedLabel.textContent = this.formatFrameColorLabel(value);
      }

      this.querySelectorAll('[data-frame-color]').forEach((button) => {
        const selected = button.dataset.frameGroup === group && button.dataset.frameColor === value;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    }

    updateFrameColorSelector() {
      if (!this.frameColorSelector) return;

      const frameValue = this.getSelectedFrameValue().toLowerCase();
      let group = '';

      if (frameValue === 'standarte' || frameValue === 'floating frame' || frameValue === 'standard' || frameValue === 'schattenfugenrahmen') {
        group = 'standarte';
      } else if (frameValue === 'gold klasike' || frameValue === 'classic gold frame' || frameValue === 'classic gold' || frameValue === 'klassischer goldrahmen') {
        group = 'gold';
      }

      this.querySelectorAll('[data-frame-color-group]').forEach((groupEl) => {
        groupEl.hidden = groupEl.dataset.frameColorGroup !== group;
      });

      if (!group) {
        this.frameColorSelector.hidden = true;
        if (this.frameColorProperty) {
          this.frameColorProperty.value = '';
          this.frameColorProperty.disabled = true;
        }
        if (this.frameColorSelectedLabel) {
          this.frameColorSelectedLabel.textContent = '';
        }
        return;
      }

      this.frameColorSelector.hidden = false;
      const defaultValue = group === 'standarte' ? 'E zezë' : 'Classic Gold';
      const value = this.frameColorSelections[group] || defaultValue;
      this.selectFrameColor(group, value);
    }

    getInputForEventTarget(target) {
      return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
    }
  }
  customElements.define("variant-picker", VariantPicker);
}